import React, { useEffect, useRef, useState } from "react";
import ChevronLeftBlackBg from "assets/svgs/ChevronLeftBlackBg";
import { useWindowResize } from "utils/hooks";
import { getFinalUrl } from "components/Search/Search";
import Mixpanel from "utils/Mixpanel";
import { getUserId } from "utils";
import { useRouter } from "next/router";
import clsx from "clsx";
import { BannersResponse } from "./types";

interface BannerProps {
	bannersResponse: BannersResponse;
}

const Banner: React.FC<BannerProps> = ({ bannersResponse }) => {
	const router = useRouter();
	const scrollContainerRef = useRef<HTMLDivElement>(null);
	const [isMobileView] = useWindowResize();
	const [activeIndex, setActiveIndex] = useState(0);
	const [displayedBanners, setDisplayedBanners] = useState<Set<any>>(new Set());

	const bannersRefs = useRef<HTMLDivElement[]>([]);

	let banners = Array.isArray(bannersResponse?.banners) ? bannersResponse.banners : [];

	const hasImageUrl = (banner, isMobile) => {
		if (banner.type === 2) {
			return isMobile
				? Boolean(banner.data.external_link?.image_url)
				: Boolean(banner.data.external_link?.image_url_web);
		}
		if (banner.type === 3) {
			return isMobile
				? Boolean(banner.data.org_search_page?.image_url)
				: Boolean(banner.data.org_search_page?.image_url_web);
		}
		return false; // disregard any other banner type
	};

	banners = banners.filter((banner) => hasImageUrl(banner, isMobileView));

	const bannersLength = banners.length;

	/* useEffect to track banner's fall within the viewport */
	useEffect(() => {
		const callback = (entries, observer) => {
			entries.forEach((entry) => {
				if (entry.isIntersecting) {
					const index = bannersRefs.current.indexOf(
						entry.target as HTMLDivElement
					);
					const bannerData = banners[index];
					if (bannerData && !displayedBanners.has(bannerData.id)) {
						Mixpanel.track("Banner shown", {
							...bannerData.analytics.raw,
							platform: "web",
							device: isMobileView ? "mobile" : "desktop",
							"User id": getUserId(),
						});
						setDisplayedBanners((prev) => new Set(prev).add(bannerData.id));
					}

					observer.unobserve(entry.target);
				}
			});
		};

		const observer = new IntersectionObserver(callback, {
			root: null,
			rootMargin: "0px",
			threshold: 0.9, // observe banner if 90% of the banner is visible
		});

		bannersRefs.current.forEach((element) => {
			if (element) {
				observer.observe(element);
			}
		});

		return () => observer.disconnect();
	}, [banners, isMobileView, displayedBanners]);

	// eslint-disable-next-line consistent-return
	useEffect(() => {
		if (
			bannersResponse?.auto_scroll?.enable &&
			((isMobileView && bannersLength > 1) ||
				(!isMobileView && bannersLength > 2)) &&
			scrollContainerRef.current
		) {
			const scrollNode = scrollContainerRef.current;
			const itemWidth = scrollNode.scrollWidth / bannersLength;

			const intervalId = setInterval(() => {
				const newScrollPosition =
					scrollNode.scrollLeft + (isMobileView ? itemWidth : itemWidth * 2);
				if (
					bannersResponse?.circular_scroll &&
					newScrollPosition >= scrollNode.scrollWidth - 100
				) {
					smoothScroll(scrollNode, 0, true);
				} else {
					smoothScroll(scrollNode, newScrollPosition, true);
				}
			}, bannersResponse?.auto_scroll?.time_period);

			return () => clearInterval(intervalId);
		}
	}, [bannersLength, bannersResponse?.auto_scroll?.enable, isMobileView]);

	const smoothScroll = (node: HTMLElement, topOrLeft: number, horizontal: boolean) => {
		if ("scrollBehavior" in document.documentElement.style) {
			node.scrollTo({
				[horizontal ? "left" : "top"]: topOrLeft,
				behavior: "smooth",
			});
		} else {
			const startTime = Date.now();
			const offset = node[horizontal ? "scrollLeft" : "scrollTop"];
			const gap = topOrLeft - offset;
			const duration = 1000;

			const step = () => {
				const elapsed = Date.now() - startTime;
				const percentage = Math.min(elapsed / duration, 1);
				// eslint-disable-next-line no-param-reassign
				node[horizontal ? "scrollLeft" : "scrollTop"] = offset + gap * percentage;
				if (percentage < 1) requestAnimationFrame(step);
			};
			step();
		}
	};

	const handleScroll = () => {
		const scrollNode = scrollContainerRef.current!;
		if (!scrollNode) return;
		const itemWidth = scrollNode.scrollWidth / bannersLength;
		const currentIndex = Math.round(scrollNode.scrollLeft / itemWidth);

		setActiveIndex(currentIndex);
	};

	useEffect(() => {
		const scrollNode = scrollContainerRef.current;

		scrollNode?.addEventListener("scroll", handleScroll);
		return () => scrollNode?.removeEventListener("scroll", handleScroll);
	}, [bannersLength]);

	// Extract image URL from both types of banners
	const getBannerImageUrl = (banner) => {
		const placeholderImageWeb = "/banner-placeholder-web.png";
		const placeholderImageMobile = "/banner-placeholder-mobile.png";

		if (!banner) return isMobileView ? placeholderImageMobile : placeholderImageWeb;

		if (banner.type === 2) {
			const { image_url: imageUrl, image_url_web: imageUrlWeb } =
				banner.data.external_link;
			return isMobileView
				? imageUrl || placeholderImageMobile
				: imageUrlWeb || placeholderImageWeb;
		}
		if (banner.type === 3) {
			const { image_url: imageUrl, image_url_web: imageUrlWeb } =
				banner.data.org_search_page;
			return isMobileView
				? imageUrl || placeholderImageMobile
				: imageUrlWeb || placeholderImageWeb;
		}

		return isMobileView ? placeholderImageMobile : placeholderImageWeb;
	};

	const handleBannerClick = (banner) => {
		if (banner) {
			Mixpanel.track("Banner clicked", {
				...banner?.analytics?.raw,
				platform: "web",
				device: isMobileView ? "mobile" : "desktop",
				"User id": getUserId(),
			});
		}

		// handling banners redirection for different types of banners
		if (banner?.type === 2 && "external_link" in banner.data) {
			window.open(banner?.data.external_link?.external_link_url, "_blank");
		} else if (banner?.type === 3 && "org_search_page" in banner.data) {
			const query = {
				entity_id: banner?.data?.org_search_page?.org.id || "",
			};
			const location = {
				identifier: banner?.data?.org_search_page.location_obj?.identifier,
				type: banner?.data?.org_search_page?.location_obj?.type,
				name: banner.data?.org_search_page?.location_obj?.name,
				id: banner?.data?.org_search_page?.location_obj?.id,
			};
			const queryString = banner?.data?.org_search_page?.org.name || "";
			window.open(getFinalUrl(query, location, queryString, {}), "_self");
		}
	};

	if (!bannersLength) return null;

	return (
		<div className="relative mx-auto mt-[8px] max-w-[1040px] md:mt-[24px]">
			{/* Left scroll button desktop */}
			{!isMobileView && bannersLength > 2 && (
				<button
					type="button"
					onClick={() => {
						const scrollNode = scrollContainerRef.current!;
						const itemWidth = scrollNode.scrollWidth / bannersLength;
						const targetScroll = scrollNode.scrollLeft - itemWidth * 2;
						smoothScroll(scrollNode, targetScroll, true);
					}}
					className="absolute left-[-36px] top-1/2 z-10 -translate-y-1/2 transform cursor-pointer rounded-full"
					aria-label="Scroll left"
				>
					<ChevronLeftBlackBg />
				</button>
			)}

			{/* Banner list */}
			<div
				ref={scrollContainerRef}
				className={clsx("flex snap-x snap-mandatory overflow-x-scroll", {
					"justify-center": isMobileView && bannersLength === 1,
				})}
				style={{ scrollbarWidth: "none" }}
			>
				{banners.map((item, index) => {
					const setRef = (el) => {
						bannersRefs.current[index] = el;
					};
					return (
						<div
							ref={setRef}
							key={item.id}
							onClick={() => handleBannerClick(item)}
							onKeyDown={(e) => {
								if (e.key === "Enter" || e.key === " ") {
									handleBannerClick(item);
								}
							}}
							tabIndex={0}
							role="button"
							aria-label={`Banner ${index + 1}`}
							style={{
								backgroundImage: `url(${getBannerImageUrl(item)})`,
								backgroundSize: "cover",
								backgroundPosition: "center",
							}}
							className={clsx(
								"md:aspect-[51:14] aspect-[82/35] w-[calc(100%-24px)] min-w-[328px] flex-shrink-0 cursor-pointer snap-center rounded-[12px] border-[1px] border-solid border-[#E8E7EA] bg-cover md:mr-[16px] md:h-[140px] md:min-w-[510px] md:flex-auto md:snap-start",
								{
									"mr-[4px]": !(isMobileView && bannersLength === 1),
									"ml-[16px] md:mr-[16px]":
										index === 0 && bannersLength !== 1,
								}
							)}
						/>
					);
				})}
				{/* Show profile update banner on web if banners are odd to make carousel even */}
				{!isMobileView && bannersLength > 0 && bannersLength % 2 !== 0 && (
					<div
						onClick={() => {
							router.push("/candidate/profile");
						}}
						onKeyDown={(e) => {
							if (e.key === "Enter" || e.key === " ") {
								router.push("/candidate/profile");
							}
						}}
						tabIndex={0}
						role="button"
						aria-label={`Banner ${bannersLength + 1}`}
						style={{
							backgroundImage: `url('https://storage.googleapis.com/mumbai_apnatime_prod/jobfeed_banners/profile_update_banner_feed.png')`,
							backgroundSize: "cover",
							backgroundPosition: "center",
						}}
						className="md:aspect-[51:14] aspect-[82/35] w-full min-w-[328px] cursor-pointer rounded-[12px] border-[1px] border-solid border-[#E8E7EA] bg-cover md:h-[140px] md:min-w-[510px] md:snap-start"
					/>
				)}
			</div>

			{/* Right scroll button desktop */}
			{!isMobileView && bannersLength > 2 && (
				<button
					type="button"
					onClick={() => {
						const scrollNode = scrollContainerRef.current!;
						const itemWidth = scrollNode.scrollWidth / bannersLength;
						let targetScroll = scrollNode.scrollLeft + itemWidth * 2;
						if (targetScroll >= scrollNode.scrollWidth) {
							if (
								bannersResponse?.circular_scroll &&
								targetScroll >= scrollNode.scrollWidth
							) {
								targetScroll = 0;
							}
						}
						smoothScroll(scrollNode, targetScroll, true);
					}}
					className="absolute right-[-32px] top-1/2 z-10 -translate-y-1/2 rotate-180 transform rounded-full"
					aria-label="Scroll right"
				>
					<ChevronLeftBlackBg />
				</button>
			)}

			{/* Scroll Indicators for Mobile */}
			{isMobileView && bannersLength > 1 && (
				<ul className="m-0 flex h-[6px] justify-center space-x-1 pb-[16px]">
					{banners.map((item, index) => (
						<li key={item.id}>
							<button
								type="button"
								className={clsx(
									"h-[6px] w-[6px] rounded-full bg-[#E8E7EA]",
									{
										"!w-[12px] !bg-[#8C8594]": activeIndex === index,
									}
								)}
								onClick={() => {
									const scrollNode = scrollContainerRef.current!;
									const itemWidth =
										scrollNode.scrollWidth / bannersLength;
									const targetScroll = itemWidth * index;

									smoothScroll(scrollNode, targetScroll, true);
									setActiveIndex(index);
								}}
								aria-pressed={activeIndex === index}
								aria-label={`Scroll to item #${index + 1}`}
							/>
						</li>
					))}
				</ul>
			)}
		</div>
	);
};

export default Banner;
